import { showcaseSource } from '@nowline/browser';

export interface Example {
    id: string;
    label: string;
    description: string;
    source: string;
}

/**
 * Example start dates are relative to the Monday of the viewer's current
 * week, so the now-line always lands where the seeded statuses say it
 * should: done work to its left, in-progress work straddling it, planned
 * work to its right. Hard-coded dates drift out of the visible range within
 * a few months.
 */
const MS_PER_DAY = 86_400_000;

/** UTC-midnight of the Monday of `today`'s local civil week. */
function mondayOf(today: Date): Date {
    const civil = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    const daysSinceMonday = (today.getDay() + 6) % 7;
    return new Date(civil - daysSinceMonday * MS_PER_DAY);
}

/** YYYY-MM-DD, `weeks` whole weeks from `monday` (negative is the past). */
function weeksFrom(monday: Date, weeks: number): string {
    return new Date(monday.getTime() + weeks * 7 * MS_PER_DAY).toISOString().slice(0, 10);
}

// Starts 5w back: the done work (Technology Selection + API) ends this week,
// and Agent Instructions is the in-progress item crossing the now-line.
const generativeAi = (monday: Date) => `nowline v1

roadmap "Generative AI" start:${weeksFrom(monday, -5)} scale:2w calendar:business

swimlane "Platform"
  item "Technology Selection" duration:2w status:done
  item api "API" duration:3w status:done
  item "Agent Instructions" duration:3w status:in-progress
  item api-integration "Agent Integration" duration:4w status:planned
  item "Agent MCP" duration:2w status:planned
  item platform-e2e-test "E2E Test" duration:2w status:planned

swimlane "Web"
  item "Web Prototype" duration:4w status:done
  item "Web SDK" duration:2w status:planned after:api
  item "Web Integration" duration:4w status:planned
  item web-e2e-test "E2E Test" duration:2w status:planned after:api-integration

swimlane "Mobile"
  parallel
    group "iOS"
      item "iOS Prototype" duration:4w status:done
      item "iOS SDK" duration:2w status:planned after:api
      item "iOS Integration" duration:4w status:planned
      item ios-e2e-test "E2E Test" duration:2w status:planned after:api-integration
    group "Android"
      item "Android Prototype" duration:4w status:done
      item "Android SDK" duration:2w status:planned after:api
      item "Android Integration" duration:4w status:planned
      item android-e2e-test "E2E Test" duration:2w status:planned after:api-integration

milestone "AI Release" after:[platform-e2e-test, web-e2e-test, ios-e2e-test, android-e2e-test]
`;

// Starts 3w back: discovery + spec are done and Build v1 is two weeks in.
const quarterlyRoadmap = (monday: Date) => `nowline v1

roadmap quarter-plan "Quarterly Roadmap" start:${weeksFrom(monday, -3)} scale:1m author:"Team"

anchor kickoff date:${weeksFrom(monday, -3)}

swimlane product "Product"
  item discovery "Discovery" duration:2w after:kickoff status:done
  item spec "Write spec" duration:1w after:discovery status:done
  item launch "Launch" duration:1w after:build

swimlane engineering "Engineering"
  item setup "Repo setup" duration:1w after:kickoff status:done
  item build "Build v1" duration:4w after:setup status:in-progress remaining:2w

milestone shipped "v1 shipped" after:launch
`;

// Starts 1w back: last week's work is done and this week's is in progress.
const sprintBoard = (monday: Date) => `nowline v1

roadmap sprint-board "Sprint Board" start:${weeksFrom(monday, -1)} scale:1w author:"Team"

anchor sprint-start date:${weeksFrom(monday, -1)}

swimlane backend "Backend"
  item auth "Auth tokens" duration:1w after:sprint-start status:done
  item api "REST endpoints" duration:2w after:auth status:in-progress remaining:1w
  item tests "Integration tests" duration:1w after:api

swimlane frontend "Frontend"
  item design "Component design" duration:1w after:sprint-start status:done
  item ui "Build UI" duration:2w after:design status:in-progress remaining:1w
  item qa "QA pass" duration:1w after:ui

milestone demo "Sprint demo" after:[tests, qa]
`;

/**
 * Seeded example roadmaps for the Examples ▾ picker, dated relative to
 * `today`. The first entry is the default document shown on fresh load.
 */
export function buildExamples(today: Date): Example[] {
    const monday = mondayOf(today);
    return [
        {
            id: 'generative-ai',
            label: 'Generative AI',
            description: 'A multi-swimlane AI project roadmap with parallel mobile tracks.',
            source: generativeAi(monday),
        },
        {
            id: 'showcase',
            label: 'Showcase',
            description: 'The canonical Nowline example with swimlanes and a milestone.',
            source: showcaseSource,
        },
        {
            id: 'quarterly-roadmap',
            label: 'Quarterly Roadmap',
            description: 'A simple two-swimlane quarterly roadmap.',
            source: quarterlyRoadmap(monday),
        },
        {
            id: 'sprint-board',
            label: 'Sprint Board',
            description: 'A backend + frontend sprint with a demo milestone.',
            source: sprintBoard(monday),
        },
    ];
}

// Dated once per page load. A tab left open across a week boundary keeps
// last week's dates until reload, which is fine for a sample.
export const EXAMPLES: Example[] = buildExamples(new Date());

export const generativeAiSource: string = EXAMPLES[0]!.source;
